import React, { useMemo } from "react";
import { RollingLeaderboard } from "../../components/tables/RollingLeaderboard";
import { BaseballTable } from "../../components/tables/BaseballTable";
import { DataControls } from "../../components/data/DataControls";
import ErrorDisplay from "../../components/alerts/ErrorDisplay";
import { InfoBanner } from "../../components/data/InfoBanner";
import { getLeaderboardColumns } from "../../config/leaderboards";
import { LEADERBOARD_TYPES } from "./constants";
import { useLeaderboardData } from "./useLeaderboardData";

const BANNER_CONFIG = {
  value: {
    title: "Value Leaderboard",
    description: "Rank players and teams by overall offensive or pitching value. Includes WAR, WPA, RE24, REW, pLI, and other metrics that measure total contribution.",
  },
  situational: {
    title: "Situational Leaderboard",
    description: "See how players and teams perform in specific game contexts: runners in scoring position, runners on base, high leverage, and other key situations.",
  },
  baserunning: {
    title: "Baserunning Leaderboard",
    description: "Evaluate baserunning impact beyond stolen bases. BsR combines weighted stolen base value (wSB), grounding-into-double-play value (wGDP), and extra-base-taken value (wXBT) to quantify total baserunning contribution in runs.",
  },
  splits: {
    title: "Splits Leaderboard",
    description: "Compare platoon splits. Break down stats by batter handedness, pitcher handedness, and venue to find matchup edges.",
  },
  battedball: {
    title: "Batted Ball Leaderboard",
    description: "Dive into batted ball profiles: ground ball, fly ball, and line drive rates, pull/oppo tendencies, and other metrics that reveal approach/quality of contact.",
  },
  rolling: {
    title: "Rolling Leaderboard",
    description: "Track recent performance trends with rolling averages. Identify who is heating up or cooling down.",
  },
};

const DEFAULT_BANNER = {
  title: "Leaderboards",
  description: "Explore comprehensive player and team statistics across all divisions.",
};

export const Leaderboards = () => {
  const {
    leaderboardType, setLeaderboardType,
    selectedYears, setSelectedYears,
    searchTerm, setSearchTerm,
    minPA, setMinPA,
    minIP, setMinIP,
    minBF, setMinBF,
    division, setDivision,
    conference, setConference,
    listId, setListId,
    playerType, setPlayerType,
    viewLevel, setViewLevel,
    conferences,
    loading,
    isInitialized,
    error,
    showError,
    filteredData,
    listPlayerIds,
    fetchData,
  } = useLeaderboardData();

  const columns = leaderboardType !== "rolling"
    ? getLeaderboardColumns(leaderboardType, playerType, viewLevel)
    : [];

  const banner = useMemo(() => BANNER_CONFIG[leaderboardType] || DEFAULT_BANNER, [leaderboardType]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <InfoBanner
          title={banner.title}
          description={banner.description}
        />

        <div className="relative z-10 mb-6">
          <DataControls
            dataType={leaderboardType}
            selectedYears={selectedYears}
            minPA={minPA}
            minIP={minIP}
            minBF={minBF}
            playerType={playerType}
            viewLevel={viewLevel}
            searchTerm={searchTerm}
            conference={conference}
            division={division}
            selectedListId={listId}
            setDataType={setLeaderboardType}
            setSelectedYears={setSelectedYears}
            setMinPA={setMinPA}
            setMinIP={setMinIP}
            setMinBF={setMinBF}
            setPlayerType={setPlayerType}
            setViewLevel={setViewLevel}
            setSearchTerm={setSearchTerm}
            setConference={setConference}
            setDivision={setDivision}
            setSelectedListId={setListId}
            conferences={conferences}
            allowedDataTypes={LEADERBOARD_TYPES.map((t) => t.id)}
            onApplyFilters={fetchData}
          />
        </div>

        <div className="relative z-10">
          {!isInitialized ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-600">Initializing...</p>
            </div>
          ) : error && showError ? (
            <div className="text-center py-12">
              <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl p-6 max-w-2xl mx-auto">
                <ErrorDisplay
                  error={{ message: error, status: 0 }}
                  context={{ division, leaderboardType }}
                  onRetry={fetchData}
                  onSwitchToDivision3={null}
                />
              </div>
            </div>
          ) : leaderboardType === "rolling" ? (
            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
              <RollingLeaderboard
                division={division}
                year={(Array.isArray(selectedYears) ? selectedYears : [selectedYears]).join(",")}
                conference={conference}
                searchTerm={searchTerm}
                selectedListId={listId}
                selectedListPlayerIds={listPlayerIds}
                isLoadingPlayerList={loading}
                playerType={playerType}
              />
            </div>
          ) : (
            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="relative">
                {loading && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2" />
                      <p className="text-sm text-gray-600">Loading data...</p>
                    </div>
                  </div>
                )}

                {filteredData.length === 0 && !loading ? (
                  <div className="text-center py-6 sm:py-12">
                    <p className="text-gray-500 text-base sm:text-lg">No data found for the current filters.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <BaseballTable
                      data={filteredData.filter((item) => item && typeof item === "object")}
                      columns={columns}
                      filename={`${leaderboardType}_${selectedYears.join("-")}.csv`}
                      stickyColumns={[0, 1]}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
